import { type ICookie } from "cookie-universal";
import { type PublicRuntimeConfig } from "@nuxt/schema";
import { DependencyInjectId } from "@@/package/const/DependencyInjectId";
import HttpClient from "@@/package/network/HttpClient";
import UserApi from "@@/package/apis/user/UserApi";
import UserMapper from "@@/package/mapper/user/UserMapper";
import CourseApi from "@@/package/apis/course/CourseApi";
import CourseMapper from "@@/package/mapper/course/CourseMapper";
import LearningApi from "@@/package/apis/learning/LearningApi";
import LearningMapper from "@@/package/mapper/learning/LearningMapper";
import TrackApi from "@@/package/apis/track/TrackApi";
import TrackMapper from "@@/package/mapper/track/TrackMapper";
import AssessmentApi from "~~/package/apis/assessment/AssessmentApi";
import AssessmentMapper from "~~/package/mapper/assessment/AssessmentMapper";
import ContestApi from "~~/package/apis/contest/ContestApi";
import ContestMapper from "~~/package/mapper/contest/ContestMapper";
import LevelAssessmentApi from "~~/package/apis/levelAssessment/LevelAssessmentApi";
import LevelAssessmentMapper from "~~/package/mapper/levelAssessment/LevelAssessmentMapper";
import ProSkillManagerMapper from "~~/package/mapper/proSkillManager/ProSkillManagerMapper";
import ProSkillManagerApi from "~~/package/apis/ProSkillManager/ProSkillManagerApi";
import ProSkillAssureApi from "~~/package/apis/ProSkillAssure/ProSkillAssureApi";
import ProSkillAssureMapper from "~~/package/mapper/proSkillAssure/ProSkillAssureMapper";
import OrganizationApi from "~~/package/apis/organization/OrganizationApi";
import OrganizationMapper from "~~/package/mapper/organization/OrganizationMapper";
import ContactApi from "~~/package/apis/contact/ContactApi";
import ContactMapper from "~~/package/mapper/contact/ContactMapper";
import ProfileApi from "~~/package/apis/Profile/ProfileApi";
import ProfileMapper from "~~/package/mapper/profile/ProfileMapper";
import HomeApi from "~~/package/apis/home/HomeApi";
import HomeMapper from "~~/package/mapper/home/HomeMapper";
import CreditApi from "~~/package/apis/credit/CreditApi";
import CreditMapper from "~~/package/mapper/credit/CreditMapper";
import ReportMapper from "~~/package/mapper/report/ReportMapper";
import ReportApi from "~~/package/apis/report/ReportApi";
import SubscriptionApi from "~~/package/apis/subscription/SubscriptionApi";
import SubscriptionMapper from "~~/package/mapper/subscription/SubscriptionMapper";
import SurveyApi from "~~/package/apis/survey/SurveyApi";
import SurveyMapper from "~~/package/mapper/survey/SurveyMapper";

interface ApplicationContextInterface {
    setServices(): void;
    getService<T>(string: any): T;
    getCookies(): ICookie;
    getConfig(): PublicRuntimeConfig;
    setCookies(cookies: ICookie): void;
}

class ApplicationContext implements ApplicationContextInterface {
    private static instance: ApplicationContextInterface;
    private _container = new Map<string, any>();
    private _factories = new Map<string, () => any>();
    private _cookies: ICookie | null = null;
    private _config: PublicRuntimeConfig | null = null;

    public static getInstance() {
        return this.instance || (this.instance = new this());
    }

    public setServices() {
        this._container.set(DependencyInjectId.HttpClient, new HttpClient(this));
        this._factories.set(DependencyInjectId.UserApi, () => new UserApi(this));
        this._factories.set(DependencyInjectId.UserMapper, () => new UserMapper(this));
        this._factories.set(DependencyInjectId.OrganizationApi, () => new OrganizationApi(this));
        this._factories.set(DependencyInjectId.OrganizationMapper, () => new OrganizationMapper(this));
        this._factories.set(DependencyInjectId.CourseApi, () => new CourseApi(this));
        this._factories.set(DependencyInjectId.CourseMapper, () => new CourseMapper(this));
        this._factories.set(DependencyInjectId.AssessmentApi, () => new AssessmentApi(this));
        this._factories.set(DependencyInjectId.AssessmentMapper, () => new AssessmentMapper(this));
        this._factories.set(DependencyInjectId.TrackApi, () => new TrackApi(this));
        this._factories.set(DependencyInjectId.TrackMapper, () => new TrackMapper(this));
        this._factories.set(DependencyInjectId.LearningApi, () => new LearningApi(this));
        this._factories.set(DependencyInjectId.LearningMapper, () => new LearningMapper(this));
        this._factories.set(DependencyInjectId.ProSkillManagerApi, () => new ProSkillManagerApi(this));
        this._factories.set(DependencyInjectId.ProSkillManagerMapper, () => new ProSkillManagerMapper(this));
        this._factories.set(DependencyInjectId.ContestApi, () => new ContestApi(this));
        this._factories.set(DependencyInjectId.ContestMapper, () => new ContestMapper(this));
        this._factories.set(DependencyInjectId.LevelAssessmentApi, () => new LevelAssessmentApi(this));
        this._factories.set(DependencyInjectId.LevelAssessmentMapper, () => new LevelAssessmentMapper(this));
        this._factories.set(DependencyInjectId.ProSkillAssureApi, () => new ProSkillAssureApi(this));
        this._factories.set(DependencyInjectId.ProSkillAssureMapper, () => new ProSkillAssureMapper(this));
        this._factories.set(DependencyInjectId.ContactApi, () => new ContactApi(this));
        this._factories.set(DependencyInjectId.ContactMapper, () => new ContactMapper(this));
        this._factories.set(DependencyInjectId.ProfileApi, () => new ProfileApi(this));
        this._factories.set(DependencyInjectId.ProfileMapper, () => new ProfileMapper(this));
        this._factories.set(DependencyInjectId.HomeApi, () => new HomeApi(this));
        this._factories.set(DependencyInjectId.HomeMapper, () => new HomeMapper(this));
        this._factories.set(DependencyInjectId.CreditApi, () => new CreditApi(this));
        this._factories.set(DependencyInjectId.CreditMapper, () => new CreditMapper(this));
        this._factories.set(DependencyInjectId.ReportApi, () => new ReportApi(this));
        this._factories.set(DependencyInjectId.ReportMapper, () => new ReportMapper(this));
        this._factories.set(DependencyInjectId.SubscriptionApi, () => new SubscriptionApi(this));
        this._factories.set(DependencyInjectId.SubscriptionMapper, () => new SubscriptionMapper(this));
        this._factories.set(DependencyInjectId.SurveyApi, () => new SurveyApi(this));
        this._factories.set(DependencyInjectId.SurveyMapper, () => new SurveyMapper(this));
    }

    public getService<T>(string: any): T {
        if (!this._container.has(string)) {
            const factory = this._factories.get(string);
            if (factory) {
                this._container.set(string, factory());
            }
        }
        return this._container.get(string);
    }

    public setCookies(cookies: ICookie): void {
        this._cookies = cookies;
    }

    public setConfig(config: PublicRuntimeConfig) {
        this._config = config;
    }

    public getConfig(): PublicRuntimeConfig {
        return this._config as PublicRuntimeConfig;
    }

    public getCookies(): ICookie {
        return this._cookies as ICookie;
    }
}

const applicationContext = ApplicationContext.getInstance();
export { applicationContext };
export type { ApplicationContextInterface };

